import { computed, ref } from "vue";
import { useMutation, useApolloClient } from "@vue/apollo-composable";
import { useToast } from "vue-toastification";
import {
  CREATE_PLANNED_TIME,
  UPDATE_PLANNED_TIME,
  DELETE_PLANNED_TIME,
  CREATE_TIME_OFF,
  UPDATE_TIME_OFF,
  DELETE_TIME_OFF,
} from "@/graphql/planning/mutations";
import {
  GET_PLANNED_TIME_FOR_EDIT,
  GET_TIME_OFF_FOR_EDIT,
} from "@/graphql/planning/queries";
import type { LeaveType } from "@/helpers/project/typesDefinition";
import type {
  PlanningFormData,
  PlanningItemType,
  PlanningProject,
  PlanningUser,
  VisibleDay,
} from "@/views/planning/type";
import type { TimesheetFormValue } from "@/helpers/client/typesDefinition";
import type {
  PlanningPlannedTime,
  PlanningTimeOff,
  PlanningTimesheetEntry,
  TimesheetSuggestion,
} from "@/helpers/planning/planningDataNormalizers";
import {
  DEFAULT_DURATION_PER_DAY,
  DEFAULT_LEAVE_TYPE,
} from "@/helpers/planning/planningDataNormalizers";

/**
 * Composable that encapsulates all planning modal state and handlers.
 * Used by both Planning.vue and EmployeeHome.vue to handle
 * create/edit of planned times, time-offs, and timesheets.
 */
export function usePlanningModals(options: {
  projects: () => PlanningProject[];
  users: () => PlanningUser[];
  onSaved: () => void;
}) {
  const showModal = ref(false);
  const isEditing = ref(false);
  const selectedItemId = ref<string | null>(null);
  const selectedItemType = ref<PlanningItemType | null>(null);
  // Fresh occurrence data fetched for editing (kept so the edit modal can display
  // the current project even when it has been archived).
  const fetchedPlannedTimeOccurrence = ref<PlanningPlannedTime | null>(null);

  const formData = ref<PlanningFormData>({
    userId: null,
    projectId: null,
    projectRoleId: null,
    startDate: "",
    endDate: "",
    frequency: null,
    repeatEndDate: null,
    durationPerDay: DEFAULT_DURATION_PER_DAY,
    notes: "",
    tentative: false,
    type: DEFAULT_LEAVE_TYPE,
  });

  const showTimesheetModal = ref(false);
  const selectedTimesheet = ref<TimesheetFormValue | null>(null);

  // --- Toast for persistent notifications ---
  const toast = useToast();

  // --- Mutations ---
  const { mutate: createPlannedTimeMutate, onDone: onPlannedTimeCreated } =
    useMutation(CREATE_PLANNED_TIME);
  onPlannedTimeCreated(() => options.onSaved());

  const { mutate: updatePlannedTimeMutate, onDone: onPlannedTimeUpdated } =
    useMutation(UPDATE_PLANNED_TIME);
  onPlannedTimeUpdated(() => options.onSaved());

  const { mutate: deletePlannedTimeMutate, onDone: onPlannedTimeDeleted } =
    useMutation(DELETE_PLANNED_TIME);
  onPlannedTimeDeleted(() => options.onSaved());

  const { mutate: createTimeOffMutate, onDone: onTimeOffCreated } =
    useMutation(CREATE_TIME_OFF);
  onTimeOffCreated(() => options.onSaved());

  const { mutate: updateTimeOffMutate, onDone: onTimeOffUpdated } =
    useMutation(UPDATE_TIME_OFF);
  onTimeOffUpdated(() => options.onSaved());

  const { mutate: deleteTimeOffMutate, onDone: onTimeOffDeleted } =
    useMutation(DELETE_TIME_OFF);
  onTimeOffDeleted(() => options.onSaved());

  // --- Helper to show persistent planning notification ---
  const showPlanningNotification = (
    type: "success" | "warning" | "error",
    message: string,
    details?: string,
  ) => {
    const toastOptions = {
      timeout: 5000, // Persistent - stays until manually closed
      closeOnClick: true,
      draggable: true,
    };

    if (details) {
      toast[type](`${message}\n${details}`, toastOptions);
    } else {
      toast[type](message, toastOptions);
    }
  };

  // --- Helpers ---
  const getUserDisplayName = (user: PlanningUser) => {
    return [user.firstName, user.lastName].filter(Boolean).join(" ");
  };

  // --- Open handlers ---
  const openNewPlannedTime = (user: PlanningUser, day: VisibleDay) => {
    isEditing.value = false;
    selectedItemId.value = null;
    selectedItemType.value = "planned_time";
    formData.value = {
      userId: user.id,
      projectId: null,
      projectRoleId: null,
      startDate: day.date,
      endDate: day.date,
      frequency: null,
      repeatEndDate: null,
      durationPerDay: DEFAULT_DURATION_PER_DAY,
      notes: "",
      tentative: false,
      type: DEFAULT_LEAVE_TYPE,
    };
    showModal.value = true;
  };

  const openNewTimeOff = (user: PlanningUser, day: VisibleDay) => {
    isEditing.value = false;
    selectedItemId.value = null;
    selectedItemType.value = "time_off";
    formData.value = {
      userId: user.id,
      projectId: null,
      startDate: day.date,
      endDate: day.date,
      frequency: null,
      repeatEndDate: null,
      durationPerDay: DEFAULT_DURATION_PER_DAY,
      notes: "",
      tentative: false,
      type: DEFAULT_LEAVE_TYPE,
    };
    showModal.value = true;
  };

  const { client } = useApolloClient();

  const openEditPlannedTime = async (plannedTime: PlanningPlannedTime) => {
    isEditing.value = true;
    selectedItemId.value = plannedTime.id;
    selectedItemType.value = "planned_time";

    // Fetch fresh data from backend to get parent planned_time info
    try {
      const { data } = await client.query({
        query: GET_PLANNED_TIME_FOR_EDIT,
        variables: { occurrenceId: plannedTime.id },
        fetchPolicy: "network-only",
      });

      const editData = data?.plannedTimeForEdit;
      fetchedPlannedTimeOccurrence.value = editData?.occurrence ?? plannedTime;
      if (editData?.parent) {
        // Use parent data for modal
        // Note: tentative is handled separately by editMode in the modal:
        // - "all" mode: null (don't update) - set by modal watcher
        // - "single" mode: occurrence's tentative - stored in fetchedPlannedTimeOccurrence
        formData.value = {
          userId: editData.occurrence.user?.id ?? plannedTime.user.id,
          projectId:
            editData.occurrence.project?.id ?? plannedTime.project?.id ?? null,
          projectRoleId: editData.occurrence.projectRole?.id ?? null,
          startDate: editData.parent.startDate,
          endDate: editData.parent.endDate,
          frequency: editData.parent.frequency,
          repeatEndDate: editData.parent.repeatEndDate,
          durationPerDay:
            editData.parent.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
          notes: editData.parent.notes ?? "",
          // Use parent's tentative as default; modal will override to null for "all" mode
          tentative: editData.parent.tentative ?? false,
          type: DEFAULT_LEAVE_TYPE,
        };
      } else {
        // Fallback to occurrence data if no parent
        formData.value = {
          userId: plannedTime.user.id,
          projectId: plannedTime.project?.id ?? null,
          projectRoleId: plannedTime.projectRole?.id ?? null,
          startDate: plannedTime.startDate,
          endDate: plannedTime.endDate,
          frequency: plannedTime.frequency ?? null,
          repeatEndDate: plannedTime.repeatEndDate ?? null,
          durationPerDay:
            plannedTime.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
          notes: plannedTime.notes ?? "",
          tentative: plannedTime.tentative ?? false,
          type: DEFAULT_LEAVE_TYPE,
        };
      }
    } catch (error) {
      console.error("Failed to fetch planned time for edit:", error);
      fetchedPlannedTimeOccurrence.value = plannedTime;
      // Fallback to occurrence data
      formData.value = {
        userId: plannedTime.user.id,
        projectId: plannedTime.project?.id ?? null,
        projectRoleId: plannedTime.projectRole?.id ?? null,
        startDate: plannedTime.startDate,
        endDate: plannedTime.endDate,
        frequency: plannedTime.frequency ?? null,
        repeatEndDate: plannedTime.repeatEndDate ?? null,
        durationPerDay: plannedTime.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
        notes: plannedTime.notes ?? "",
        tentative: plannedTime.tentative ?? false,
        type: DEFAULT_LEAVE_TYPE,
      };
    }

    showModal.value = true;
  };

  const openEditTimeOff = async (timeOff: PlanningTimeOff) => {
    isEditing.value = true;
    selectedItemId.value = timeOff.id;
    selectedItemType.value = "time_off";

    // Fetch fresh data from backend to get parent time_off info
    try {
      const { data } = await client.query({
        query: GET_TIME_OFF_FOR_EDIT,
        variables: { occurrenceId: timeOff.id },
        fetchPolicy: "network-only",
      });

      const editData = data?.timeOffForEdit;
      if (editData?.parent) {
        // Use parent data for modal
        formData.value = {
          userId: editData.occurrence.user?.id ?? timeOff.user.id,
          projectId: null,
          startDate: editData.parent.startDate,
          endDate: editData.parent.endDate,
          frequency: editData.parent.frequency,
          repeatEndDate: editData.parent.repeatEndDate,
          durationPerDay:
            editData.parent.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
          notes: "",
          tentative: false,
          type: (editData.parent.type ?? DEFAULT_LEAVE_TYPE) as LeaveType,
        };
      } else {
        // Fallback to occurrence data if no parent
        formData.value = {
          userId: timeOff.user.id,
          projectId: null,
          startDate: timeOff.startDate,
          endDate: timeOff.endDate,
          frequency: timeOff.frequency ?? null,
          repeatEndDate: timeOff.repeatEndDate ?? null,
          durationPerDay: timeOff.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
          notes: "",
          tentative: false,
          type: (timeOff.type ?? DEFAULT_LEAVE_TYPE) as LeaveType,
        };
      }
    } catch (error) {
      console.error("Failed to fetch time off for edit:", error);
      // Fallback to occurrence data
      formData.value = {
        userId: timeOff.user.id,
        projectId: null,
        startDate: timeOff.startDate,
        endDate: timeOff.endDate,
        frequency: timeOff.frequency ?? null,
        repeatEndDate: timeOff.repeatEndDate ?? null,
        durationPerDay: timeOff.durationPerDay ?? DEFAULT_DURATION_PER_DAY,
        notes: "",
        tentative: false,
        type: (timeOff.type ?? DEFAULT_LEAVE_TYPE) as LeaveType,
      };
    }

    showModal.value = true;
  };

  const openNewTimesheet = (user: PlanningUser, day: VisibleDay) => {
    selectedTimesheet.value = {
      date: day.date,
      duration: 0,
      notes: "",
      userId: user.id,
      userName: getUserDisplayName(user),
      projectId: null,
      themeId: null,
      roleId: null,
      activityId: null,
    };
    showTimesheetModal.value = true;
  };

  const openNewTimesheetFromSuggestion = (suggestion: TimesheetSuggestion) => {
    const projectId = suggestion.plannedTime.project?.id ?? null;

    selectedTimesheet.value = {
      date: suggestion.day.date,
      duration: Math.round(suggestion.hours * 60),
      notes: "",
      userId: suggestion.user.id,
      userName: getUserDisplayName(suggestion.user),
      projectId,
      themeId: null,
      roleId: suggestion.plannedTime.projectRole?.id ?? null,
      activityId: null,
    };

    showTimesheetModal.value = true;
  };

  const openEditTimesheet = (timesheetEntry: PlanningTimesheetEntry) => {
    selectedTimesheet.value = {
      id: timesheetEntry.id,
      date: timesheetEntry.date,
      duration: timesheetEntry.duration,
      notes: timesheetEntry.notes ?? "",
      userId: timesheetEntry.user.id,
      userName: getUserDisplayName(timesheetEntry.user),
      projectId: timesheetEntry.project.id,
      themeId: timesheetEntry.theme.id,
      roleId: timesheetEntry.projectRole.id,
      activityId: null,
    };
    showTimesheetModal.value = true;
  };

  // --- Close handlers ---
  const closeModal = () => {
    showModal.value = false;
    selectedItemId.value = null;
    selectedItemType.value = null;
    fetchedPlannedTimeOccurrence.value = null;
  };

  // The project currently being edited. Prefers the fresh data from the
  // plannedTimeForEdit query (which includes archived projects), falls back to
  // the active projects list. Used so the readonly project dropdown in the edit
  // modal can still display an archived project.
  const currentProject = computed<PlanningProject | null>(() => {
    if (!isEditing.value || selectedItemType.value !== "planned_time") {
      return null;
    }

    const projectId = formData.value.projectId;
    if (!projectId) {
      return null;
    }

    const occurrenceProject = fetchedPlannedTimeOccurrence.value?.project;
    if (occurrenceProject && occurrenceProject.id === projectId) {
      return occurrenceProject;
    }

    return (
      options.projects().find((project) => project.id === projectId) ?? null
    );
  });

  const closeTimesheetModal = () => {
    showTimesheetModal.value = false;
    selectedTimesheet.value = null;
  };

  // --- Timesheet update handlers ---
  const handleTimesheetUpdated = () => {
    options.onSaved();
    closeTimesheetModal();
  };

  const handleTimesheetDeleted = () => {
    options.onSaved();
    closeTimesheetModal();
  };

  // --- Save / Delete planning items ---
  const savePlanningItem = async () => {
    if (!selectedItemType.value) {
      console.error("Missing item type");
      closeModal();
      return;
    }

    const fd = formData.value;
    const isPlannedTime = selectedItemType.value === "planned_time";

    const userId = fd.userId;
    const startDate = fd.startDate;
    const endDate = fd.endDate;
    const projectId = isPlannedTime ? fd.projectId : null;

    if (!userId || !startDate || !endDate || (isPlannedTime && !projectId)) {
      console.error("Missing required fields for saving planning item", {
        type: selectedItemType.value,
        fd,
      });
      return;
    }

    const input: Record<string, unknown> = {
      userId,
      startDate,
      endDate,
      frequency: fd.frequency || "no_repeat",
      repeatEndDate: fd.repeatEndDate || null,
      durationPerDay: fd.durationPerDay || DEFAULT_DURATION_PER_DAY,
    };

    if (isPlannedTime) {
      input.projectId = projectId;
      input.projectRoleId = fd.projectRoleId ?? null;
      input.tentative = fd.tentative || false;
    }

    if (selectedItemType.value === "time_off") {
      input.type = fd.type ?? DEFAULT_LEAVE_TYPE;
    }

    try {
      let response;

      console.log("DEBUG: Saving planning item", {
        type: selectedItemType.value,
        selectedItemId: selectedItemId.value,
      });

      if (selectedItemType.value === "planned_time") {
        if (selectedItemId.value) {
          const result = await updatePlannedTimeMutate({
            input: { id: selectedItemId.value, ...input },
          });
          response = result?.data?.updatePlannedTime;
        } else {
          const result = await createPlannedTimeMutate({ input });
          response = result?.data?.createPlannedTime;
        }
      }

      if (selectedItemType.value === "time_off") {
        if (selectedItemId.value) {
          const result = await updateTimeOffMutate({
            input: { id: selectedItemId.value, ...input },
          });
          response = result?.data?.updateTimeOff;
        } else {
          const result = await createTimeOffMutate({ input });
          response = result?.data?.createTimeOff;
        }
      }

      console.log("DEBUG: Response received", {
        response,
        hasData: !!response?.data,
      });

      if (!response || response.status !== "ok") {
        console.error(
          "Failed to save planning item:",
          response?.error ?? response,
        );
        // Show error notification for failed save
        if (selectedItemType.value === "time_off" && response?.error) {
          showPlanningNotification(
            "error",
            "Failed to create time off",
            response.error,
          );
        }
        return;
      }

      // Show notification for planned time with adjustment info
      if (selectedItemType.value === "planned_time" && response.data) {
        const data = response.data;
        console.log("DEBUG: Planned time response data", { data });

        const hasAdjustments =
          (data.adjustedDates && data.adjustedDates.length > 0) ||
          (data.skippedDates && data.skippedDates.length > 0);

        if (hasAdjustments) {
          const details: string[] = [];
          if (data.adjustedDates?.length > 0) {
            details.push(`${data.adjustedDates.length} date(s) adjusted`);
          }
          if (data.skippedDates?.length > 0) {
            details.push(
              `${data.skippedDates.length} date(s) skipped (no capacity)`,
            );
          }

          showPlanningNotification(
            "warning",
            "Planned time created with adjustments",
            details.join(", "),
          );
        }
      }

      // Show notification for time off with adjustment info
      if (selectedItemType.value === "time_off" && response.data) {
        const data = response.data;
        const hasAdjustments =
          (data.adjustedDates && data.adjustedDates.length > 0) ||
          (data.skippedDates && data.skippedDates.length > 0) ||
          (data.deletedPlannedTimeIds &&
            data.deletedPlannedTimeIds.length > 0) ||
          (data.reducedPlannedTimeIds && data.reducedPlannedTimeIds.length > 0);

        if (hasAdjustments) {
          const details: string[] = [];
          if (data.adjustedDates?.length > 0) {
            details.push(`${data.adjustedDates.length} date(s) adjusted`);
          }
          if (data.skippedDates?.length > 0) {
            details.push(`${data.skippedDates.length} date(s) skipped`);
          }
          if (data.deletedPlannedTimeIds?.length > 0) {
            details.push(
              `${data.deletedPlannedTimeIds.length} planned time(s) deleted`,
            );
          }
          if (data.reducedPlannedTimeIds?.length > 0) {
            details.push(
              `${data.reducedPlannedTimeIds.length} planned time(s) reduced`,
            );
          }

          showPlanningNotification(
            "warning",
            "Time off created with adjustments",
            details.join(", "),
          );
        } else {
          showPlanningNotification("success", "Time off created successfully");
        }
      }

      closeModal();
    } catch (error) {
      console.error("Error saving planning item:", error);
    }
  };

  const deletePlanningItem = async () => {
    if (!selectedItemId.value || !selectedItemType.value) {
      closeModal();
      return;
    }

    try {
      if (selectedItemType.value === "planned_time") {
        await deletePlannedTimeMutate({ id: selectedItemId.value });
      } else if (selectedItemType.value === "time_off") {
        await deleteTimeOffMutate({ id: selectedItemId.value });
      }
    } catch (error) {
      console.error("Error deleting planning item:", error);
    }

    closeModal();
  };

  return {
    // Modal state
    showModal,
    isEditing,
    formData,
    selectedItemType,
    showTimesheetModal,
    selectedTimesheet,
    // Current project being edited (may be archived)
    currentProject,
    // Computed for template
    timesheetModalKey: computed(() =>
      selectedTimesheet.value
        ? [
            selectedTimesheet.value.id ?? "new",
            selectedTimesheet.value.userId ?? "current",
            selectedTimesheet.value.date,
            selectedTimesheet.value.projectId ?? "no-project",
          ].join("-")
        : "",
    ),
    // Open handlers
    openNewPlannedTime,
    openNewTimeOff,
    openEditPlannedTime,
    openEditTimeOff,
    openNewTimesheet,
    openNewTimesheetFromSuggestion,
    openEditTimesheet,
    // Close handlers
    closeModal,
    closeTimesheetModal,
    // Action handlers
    savePlanningItem,
    deletePlanningItem,
    handleTimesheetUpdated,
    handleTimesheetDeleted,
  };
}
